import {
  getDeadlineQuarterEndDate,
  getDeadlineQuarterKey,
  NO_DEADLINE_QUARTER_KEY,
  parseDeadlineQuarterKey,
} from '@griply/utils';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalCardMultiSelectContextMenu } from 'shared/components/connected/goal-card-multi-select-context-menu';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { Goal, UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { InheritColors } from 'shared/types/inherit-colors';

import { ARCHIVED_SECTION_ID } from '../../constants';
import { useContextMenu } from '../../context-menu-context';
import { useDeadlineGoalListTree } from '../../hooks/use-deadline-goal-list-tree';
import { GroupingGoalListProps } from '../../types';
import { GoalSectionHeader } from '../goal-section-header';
import { TreeGoalCard } from '../tree-goal-card';
import * as Styled from './deadline-grouping-goal-list.style';

const droppedTypes = {
  [TaskListItemTypes.Root]: [],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

const dragTypes = [TaskListItemTypes.Goal];

export const DeadlineGroupingGoalList: React.FC<GroupingGoalListProps> = ({
  goals,
  collapsedGoals,
  onGoalCollapse,
  scrollElement,
  onReorder,
  onUpdateProgress,
  hideArchived,
  hideCompleted,
  isMultiSelectModifierPressed,
  excludedContextMenuOptions,
}) => {
  const { t } = useTranslation();

  const goalsMap = useMemo(
    () =>
      goals.reduce<Record<ID, Goal>>((acc, goal) => {
        acc[goal.id] = goal;
        return acc;
      }, {}),
    [goals],
  );

  const treeItems = useDeadlineGoalListTree(goals, {
    hideArchived,
    hideCompleted,
  });

  const sectionTitle = useCallback(
    (sectionId: ID) => {
      if (sectionId === ARCHIVED_SECTION_ID) {
        return t('pages.goals.archived.title');
      }
      if (sectionId === NO_DEADLINE_QUARTER_KEY) {
        return t(
          'context-menus.filtering.task.filters.date.presets.no-deadline',
        );
      }
      const parsed = parseDeadlineQuarterKey(sectionId);
      return parsed ? `Q${parsed.quarter} ${parsed.year}` : sectionId;
    },
    [t],
  );

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: UpdateGoal[] = [];

    updatedItems.forEach((item) => {
      if (item.type !== TaskListItemTypes.Goal || !goalsMap[item.id]) {
        return;
      }

      const goalToUpdate: UpdateGoal = { id: item.id };
      const parentItem =
        updatedItems.find(({ children }) => children?.includes(item.id)) ??
        Object.values(treeItems).find(({ children }) =>
          children?.includes(item.id),
        );

      // order of this goal's own sub-goals
      goalToUpdate.goalSorting = item.children ?? null;

      const parentGoal = parentItem ? goalsMap[parentItem.id] : undefined;
      if (parentGoal) {
        // dropped onto another goal → becomes a sub-goal
        goalToUpdate.parentIds = [
          ...(parentGoal.parentIds ?? []),
          parentGoal.id,
        ];
        goalToUpdate.lifeAreaId = null;
        goalToUpdate.color =
          goalsMap[item.id].color === InheritColors.LifeArea
            ? InheritColors.Goal
            : goalsMap[item.id].color;
        updatedDocs.push(goalToUpdate);
        return;
      }

      // root goal inside a quarter section → reschedule the deadline to match
      goalToUpdate.parentIds = null;
      const sectionKey = parentItem?.id;
      if (sectionKey && sectionKey !== ARCHIVED_SECTION_ID) {
        const currentKey = getDeadlineQuarterKey(goalsMap[item.id].deadline);
        if (sectionKey !== currentKey) {
          goalToUpdate.deadline =
            sectionKey === NO_DEADLINE_QUARTER_KEY
              ? null
              : getDeadlineQuarterEndDate(sectionKey);
        }
      }

      updatedDocs.push(goalToUpdate);
    });

    // Persist the flat root-goal order (across all quarter sections) as the
    // roadmap custom order — useGoalTreeReorder routes rootId → goalRoadmapSorting.
    const merged: Record<ID, TreeItem> = { ...treeItems };
    updatedItems.forEach((item) => {
      merged[item.id] = item;
    });
    const flatRootGoals = (merged[rootId]?.children ?? [])
      .filter((sectionId) => sectionId !== ARCHIVED_SECTION_ID)
      .flatMap((sectionId) => merged[sectionId]?.children ?? []);

    onReorder([{ id: rootId, goalSorting: flatRootGoals }], updatedDocs);
  };

  const { close, position, payload } = useContextMenu();

  const renderItem = useCallback(
    ({ item, itemInstance, tree }: RenderItemProps) => {
      const itemId = itemInstance.getItemData().sourceId;
      const itemData = treeItems[itemId];

      if (!itemData) {
        return null;
      }

      if (itemData.type === TaskListItemTypes.Goal) {
        const goal = goalsMap[itemId];
        if (!goal) {
          return null;
        }

        return (
          <TreeGoalCard
            item={itemInstance}
            goal={goal}
            tree={tree}
            onUpdateProgress={onUpdateProgress}
            isMultiSelectModifierPressed={isMultiSelectModifierPressed}
            excludedContextMenuOptions={excludedContextMenuOptions}
          />
        );
      }

      if (item.type === TaskListItemTypes.LifeArea) {
        const rootChildren = treeItems.root.children ?? [];
        const isFirst = rootChildren[0] === itemId;

        return (
          <GoalSectionHeader
            item={itemInstance}
            lifeArea={undefined}
            overrideTitle={sectionTitle(itemId)}
            overrideIcon={null}
            disableLink
            isFirst={isFirst}
          />
        );
      }

      return null;
    },
    [
      treeItems,
      goalsMap,
      onUpdateProgress,
      isMultiSelectModifierPressed,
      excludedContextMenuOptions,
      sectionTitle,
    ],
  );

  if (!goals.length) {
    return null;
  }

  return (
    <Styled.Container>
      <VirtualizedTreeList
        items={treeItems}
        rootItemId={rootId}
        renderItem={renderItem}
        scrollElement={scrollElement}
        collapsedItems={collapsedGoals}
        onCollapse={onGoalCollapse}
        onReorder={_onReorder}
        dropTypes={droppedTypes}
        dragTypes={dragTypes}
        indentationWidth={2.5}
      />
      {position && payload && (
        <GoalCardMultiSelectContextMenu
          goals={payload.goals}
          location={position}
          onClose={close}
          initialActiveSubMenu={payload.initialActiveSubMenu}
        />
      )}
    </Styled.Container>
  );
};
